'use client'

import { IAddressBook, IMetaWrapper, TravelPointQuoteWithAddressId, useUpFetch } from '@repo/sdk'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  AddNewAddressForm,
  Button,
  DeleteAddress,
  EditAddress,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  useAddressBookContext,
} from '@repo/ui'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Bookmark } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useInView } from 'react-intersection-observer'

interface Props {
  index: number
}

export const AddressBook = ({ index }: Props) => {
  const [open, setOpen] = useState(false)
  const { control, setValue } = useFormContext<{ drops: TravelPointQuoteWithAddressId[] }>()

  const { field } = useController({
    control,
    name: `drops.${index}`,
  })

  const [selectedAddress, setSelectedAddress] = useState<TravelPointQuoteWithAddressId>(field.value)

  const onAddressSelect = (data: IAddressBook) => {
    setSelectedAddress({
      ...field.value,
      addressId: data.address.id,
      place: {
        ...field.value.place,
        ...data.address,
        country: data.address.country_code,
        name: data.name,
      },
    })
  }

  const setField = () => {
    setValue(`drops.${index}`, selectedAddress)
    setOpen(false)
  }

  return (
    <Sheet onOpenChange={setOpen} open={open}>
      <SheetTrigger asChild>
        <Button size={'icon'} variant={'outline'}>
          <Bookmark />
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Company addresses</SheetTitle>
          <AddNewAddressForm />
        </SheetHeader>
        <AddressesAccordion onSelect={onAddressSelect} />
        <SheetFooter>
          <Button className={'w-full'} onClick={() => setOpen(false)} variant={'secondary'}>
            Cancel
          </Button>
          <Button
            className={'w-full'}
            disabled={!selectedAddress.addressId}
            onClick={() => setField()}
          >
            Use contact
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

interface AddressesAccordionProps {
  onSelect: (data: IAddressBook) => void
}

const AddressesAccordion = ({ onSelect }: AddressesAccordionProps) => {
  const { inView, ref } = useInView()
  const { companyId } = useAddressBookContext()
  const upFetch = useUpFetch()

  const baseParams = {
    perPage: 40,
    'sort[name]': 'asc',
  }

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    initialPageParam: 1,  //eslint-disable-line
    //eslint-disable-next-line perfectionist/sort-objects
    queryFn: async ({ pageParam }) => {
      return await upFetch<IMetaWrapper<IAddressBook>>(`companies/${companyId}/address-book`, {
        params: {
          ...baseParams,
          page: pageParam,
        },
      })
    },
    //eslint-disable-next-line perfectionist/sort-objects
    queryKey: ['/companies/address-book', companyId, baseParams],
    getNextPageParam: lastPage => lastPage.meta.current_page === lastPage.meta.last_page ? undefined : lastPage.meta.current_page + 1, //eslint-disable-line
  })

  useEffect(() => {
    if (inView) {
      fetchNextPage()
    }
  }, [fetchNextPage, inView])

  // todo improve O(n^2) to O(n)
  const groupedAddresses = useMemo(() => {
    const groups: Record<string, IAddressBook[]> = {}
    if (data?.pages) {
      data.pages.forEach(page => {
        page.data.forEach(address => {
          const firstLetter = address.name[0].toUpperCase()
          if (!groups[firstLetter]) {
            groups[firstLetter] = []
          }
          groups[firstLetter].push(address)
        })
      })
    }
    return groups
  }, [data])

  const flattenedAddresses = useMemo(() => {
    if (!data?.pages) return []
    return data.pages.flatMap(page => page.data)
  }, [data])

  const onAddressSelect = (id: string) => {
    const selectedAddress = flattenedAddresses.find(address => address.id === Number(id))

    if (!selectedAddress) return

    onSelect(selectedAddress)
  }

  return (
    <div className={'py-4'}>
      <ScrollArea className={'h-[calc(100svh-190px)]'}>
        <Accordion collapsible onValueChange={onAddressSelect} type={'single'}>
          {Object.entries(groupedAddresses).map(([letter, addresses]) => (
            <div className={'py-4'} key={letter}>
              <p className={'text-xl font-semibold'}>{letter}</p>
              {addresses.map(address => (
                <AccordionItem
                  className={'border-none'}
                  key={address.id}
                  value={address.id.toString()}
                >
                  <AccordionTrigger className={'border-b py-1 text-left'}>
                    {address.name}
                  </AccordionTrigger>
                  <AccordionContent className={'flex w-full items-center justify-between'}>
                    <div>
                      <p>{address.address.country}</p>
                      <p>
                        {address.address.city} {address.address.postcode}
                      </p>
                      <p>{address.address.postcode}</p>
                    </div>
                    <div className={'flex flex-col gap-1'}>
                      <EditAddress
                        address={address.address}
                        companyId={companyId}
                        id={address.id}
                      />
                      <DeleteAddress
                        address={address.address}
                        companyId={companyId}
                        id={address.id}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </div>
          ))}
        </Accordion>
        {hasNextPage && (
          <Button
            disabled={!hasNextPage || isFetchingNextPage}
            onClick={() => fetchNextPage()}
            ref={ref as any}
            variant={'ghost'}
          >
            {isFetchingNextPage ? 'Loading more...' : hasNextPage ? 'Load Newer' : ''}
          </Button>
        )}
      </ScrollArea>
    </div>
  )
}
